import { useMemo, useState } from 'react';
import type { TColumn, TRow } from './guide.types';
import { Select, SelectItem } from 'components/ui/select';
import { useI18n } from 'context/i18n';
import { useProductSheet } from 'context/product-sheet';
import { useLocale } from 'hooks/useLocale';
import { COUNTRIES_USING_INCHES, UNIT_LABELS_MAPPER } from 'utils/constants/sizes';

export function useSizeGuide() {
  const { t } = useI18n();
  const {
    productSheet: { sizesGuide, realTarget },
  } = useProductSheet();
  // el mercado es EU, pero el pais es ES por ejemplo
  const { country, market } = useLocale();

  const [units, setUnits] = useState(() => {
    if (COUNTRIES_USING_INCHES.includes(country)) {
      return 'in';
    } else {
      return country === 'KR' ? 'mm' : 'cm';
    }
  });
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const guidesTargets = useMemo(() => sizesGuide.map((item) => ({ id: item.targetId, name: item.targetAnchor })), [sizesGuide]);
  const [selectedTarget, setSelectedTarget] = useState(() => {
    if (sizesGuide.find((g) => g.targetId === realTarget)) return realTarget;
    return guidesTargets[0].id;
  });

  const currentGuide = useMemo(() => sizesGuide.find((item) => item.targetId === selectedTarget), [sizesGuide, selectedTarget]);
  const countryValues = useMemo(() => {
    if (currentGuide.productType === 'shoes') {
      return Object.values(currentGuide.guideSizes)[0]?.reduce(
        (acc, guideValue) => {
          if (guideValue.country === market) return acc;
          acc.push({ id: guideValue.country, name: guideValue.country });
          return acc;
        },
        [] as { id: string; name: string }[],
      );
    }
    return [];
  }, [currentGuide.guideSizes, market, currentGuide.productType]);

  const cols = useMemo(() => {
    const c: TColumn[] = [];
    if (currentGuide.productType === 'shoes') {
      // the first column is the foot length
      c.push({
        key: `footLength`,
        title: t('ficha.producto', `feet.length${UNIT_LABELS_MAPPER[units]}`),
      });
      // then the market column
      c.push({
        key: market,
        title: market,
      });
      // then the country column
      c.push({
        key: 'country.select',
        title: 'country',
        render: () => {
          return (
            <Select items={countryValues} selectedKey={selectedCountry} onSelectionChange={(key: string) => setSelectedCountry(key)} variant="empty">
              {(item) => (
                <SelectItem key={item.id} id={item.id}>
                  {item.name}
                </SelectItem>
              )}
            </Select>
          );
        },
      });
      // then the socks if they exist
      if (currentGuide.socksSizes) {
        c.push({
          key: 'socks',
          title: t('ficha.producto', 'socks'),
        });
      }
      return c;
    }
    // si no es un zapato sacamos las columnas de los valores size del guideSizes
    // la primera columna esta vaccia y es el nombre de la parte
    c.push({
      key: 'part',
      title: '',
    });
    // las siguientes columnas son las tallas
    currentGuide.guideSizes[Object.keys(currentGuide.guideSizes)[0]].forEach((size) => {
      c.push({
        key: size.size,
        title: size.size,
      });
    });
    return c;
  }, [countryValues, currentGuide.guideSizes, currentGuide.productType, currentGuide.socksSizes, market, selectedCountry, t, units]);

  const rows = useMemo(() => {
    if (currentGuide.productType === 'shoes') {
      const addedSockSize = new Set([]);
      const guideToUse = currentGuide.guideSizes;

      const rows = Object.entries(guideToUse)

        .reduce((acc, [footLength, footLengthSizes]) => {
          const marketSize = footLengthSizes.find((size) => size.country === market)?.size ?? '';
          const rowData = footLengthSizes.reduce(
            (acc, size) => {
              acc[size.country] = size.size;
              return acc;
            },
            {} as Record<string, string>,
          );

          const rowToPush: TRow = [];
          rowToPush.push({
            key: 'footLength',
            value: units === 'in' && currentGuide.equivalences[footLength] ? currentGuide.equivalences[footLength] : footLength,
            rowData,
          });
          rowToPush.push({
            key: market,
            value: marketSize,
            rowData,
          });
          rowToPush.push({
            key: 'country.select',
            value: '',
            rowData,
            className: 'w-12',
            render: (rowData) => {
              return rowData[selectedCountry];
            },
          });
          if (currentGuide.socksSizes) {
            const currentSockSize = Object.entries(currentGuide.socksSizes).find(([size, sizes]) => sizes.includes(rowData['EU']))?.[0];
            if (currentSockSize) {
              addedSockSize.add(currentSockSize);
              rowToPush.push({
                key: 'socks',
                value: currentSockSize,
                rowData,
                rowSpan: addedSockSize.has(currentGuide) ? currentGuide.socksSizes[currentSockSize].length : 0,
              });
            }
          }
          return [...acc, rowToPush];
        }, [] as TRow[])
        .sort((a, b) => {
          const euSizeA = Number(a[0].rowData['EU']);
          const euSizeB = Number(b[0].rowData['EU']);
          return euSizeA - euSizeB;
        });

      return rows;
    }
    return Object.entries(currentGuide.guideSizes).map(([part, partSizes]) => {
      const rowData = partSizes.reduce(
        (acc, size) => {
          acc[size.size] = size.size;
          return acc;
        },
        {} as Record<string, string>,
      );
      const row: TRow = [];
      row.push({
        key: 'part',
        value: part,
        rowData,
      });
      partSizes.forEach((size) => {
        let rowValue = size.sizeValue;
        const splitValue = size.sizeValue.split(' / ');
        if (splitValue.length === 2) {
          rowValue = splitValue[units === 'cm' ? 0 : 1];
        }
        row.push({
          key: size.size,
          value: rowValue,
          rowData,
        });
      });
      return row;
    });
  }, [currentGuide, market, selectedCountry, units]);

  return {
    units,
    setUnits,
    currentGuide,
    guidesTargets,
    selectedTarget,
    setSelectedTarget,
    cols,
    rows,
  };
}
